import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Space, Modal, Input, Select, App, Tag, Typography, Descriptions } from 'antd';
import type { TableProps } from 'antd';
import { RollbackOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { get, post } from '../../api/client';

const { Text } = Typography;

interface PhoneReleaseLog {
  id: string;
  phone: string;
  oldPartyId: string;
  oldPartyFullName: string;
  oldPartyEmail: string | null;
  oldPartyField: string;
  newPartyId: string | null;
  newFullName: string;
  newIdCard: string | null;
  smsCodeSentAt: string;
  verifiedAt: string;
  releasedAt: string | null;
  notifiedOldOwner: boolean;
  rolledBackAt: string | null;
  rollbackReason: string | null;
  status: string;
  phoneReleaseLogCreatedAt: string;
}

interface ListResult {
  total: number;
  page: number;
  pageSize: number;
  records: PhoneReleaseLog[];
}

const STATUS_META: Record<string, { label: string; color: string }> = {
  pending: { label: '待确认', color: 'orange' },
  released: { label: '已释放', color: 'green' },
  rolled_back: { label: '已回滚', color: 'default' },
};

export default function PhoneReleasesPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [rollbackTarget, setRollbackTarget] = useState<PhoneReleaseLog | null>(null);
  const [rollbackReason, setRollbackReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['phone-releases', status, keyword, page],
    queryFn: () =>
      get<ListResult>('/admin/phone-releases', {
        status,
        keyword: keyword || undefined,
        page,
        pageSize,
      }),
  });

  const rollback = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      post(`/admin/phone-releases/${id}/rollback`, { reason }),
    onSuccess: () => {
      message.success('回滚成功，号码已归还旧持有人');
      setRollbackTarget(null);
      setRollbackReason('');
      void queryClient.invalidateQueries({ queryKey: ['phone-releases'] });
    },
    onError: (e) => message.error(e instanceof Error ? e.message : '回滚失败'),
  });

  const columns: TableProps<PhoneReleaseLog>['columns'] = [
    { title: '手机号', dataIndex: 'phone', width: 130, render: (v: string) => <Text copyable>{v}</Text> },
    {
      title: '旧持有人',
      key: 'oldParty',
      width: 180,
      render: (_, r: PhoneReleaseLog) => (
        <Space size={4}>
          <span>{r.oldPartyFullName}</span>
          <Tag style={{ marginInlineEnd: 0 }}>{r.oldPartyField === 'phone' ? '主号' : '备号'}</Tag>
        </Space>
      ),
    },
    {
      title: '新注册用户',
      key: 'newParty',
      width: 180,
      render: (_, r: PhoneReleaseLog) => (
        <span>
          {r.newFullName}
          {r.newIdCard ? <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>{r.newIdCard}</Text> : null}
        </span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      filters: [
        { text: '待确认', value: 'pending' },
        { text: '已释放', value: 'released' },
        { text: '已回滚', value: 'rolled_back' },
      ],
      render: (v: string) => {
        const meta = STATUS_META[v] ?? { label: v, color: 'default' };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: '验证时间',
      dataIndex: 'verifiedAt',
      width: 150,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: '释放时间',
      dataIndex: 'releasedAt',
      width: 150,
      render: (v: string | null) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: '回滚信息',
      key: 'rollback',
      width: 240,
      render: (_, r: PhoneReleaseLog) =>
        r.rollbackReason ? (
          <span title={r.rollbackReason} style={{ display: 'block' }} className="admin-ellipsis">
            {r.rolledBackAt ? dayjs(r.rolledBackAt).format('MM-DD HH:mm') : ''}
            <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>{r.rollbackReason}</Text>
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, r: PhoneReleaseLog) =>
        r.status === 'released' ? (
          <Button
            size="small"
            danger
            icon={<RollbackOutlined />}
            onClick={() => {
              setRollbackTarget(r);
              setRollbackReason('');
            }}
          >
            回滚
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      title="手机号释放审计"
      extra={
        <Space>
          <Select
            style={{ width: 140 }}
            allowClear
            placeholder="全部状态"
            value={status}
            onChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
            options={[
              { value: 'pending', label: '待确认' },
              { value: 'released', label: '已释放' },
              { value: 'rolled_back', label: '已回滚' },
            ]}
          />
          <Input.Search
            style={{ width: 240 }}
            allowClear
            placeholder="搜索手机号 / 姓名"
            prefix={<SearchOutlined style={{ color: 'rgba(0,0,0,0.25)' }} />}
            onSearch={(v) => {
              setKeyword(v.trim());
              setPage(1);
            }}
          />
          <Button icon={<ReloadOutlined />} onClick={() => queryClient.invalidateQueries({ queryKey: ['phone-releases'] })} />
        </Space>
      }
    >
      <Table<PhoneReleaseLog>
        rowKey="id"
        loading={isLoading}
        dataSource={data?.records ?? []}
        columns={columns}
        scroll={{ x: 1230 }}
        pagination={{
          current: page,
          pageSize,
          total: data?.total ?? 0,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (p) => setPage(p),
        }}
      />

      <Modal
        title="回滚手机号释放"
        open={rollbackTarget !== null}
        onCancel={() => setRollbackTarget(null)}
        onOk={() => rollbackTarget && rollback.mutate({ id: rollbackTarget.id, reason: rollbackReason || '管理员手动回滚' })}
        confirmLoading={rollback.isPending}
        okButtonProps={{ danger: true }}
        okText="确认回滚"
      >
        {rollbackTarget && (
          <>
            <Descriptions size="small" column={1} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="手机号">{rollbackTarget.phone}</Descriptions.Item>
              <Descriptions.Item label="归还给">
                {rollbackTarget.oldPartyFullName}（{rollbackTarget.oldPartyField === 'phone' ? '主号' : '备号'}）
              </Descriptions.Item>
            </Descriptions>
            {rollbackTarget.newPartyId && (
              <Text type="warning" style={{ display: 'block', marginBottom: 12 }}>
                若新用户仍将该号码用作主手机号，回滚将被拒绝；请先要求其更换主手机号。
              </Text>
            )}
            <Input.TextArea
              rows={3}
              maxLength={200}
              placeholder="回滚原因（默认：管理员手动回滚）"
              value={rollbackReason}
              onChange={(e) => setRollbackReason(e.target.value)}
            />
          </>
        )}
      </Modal>
    </Card>
  );
}